import axios from 'axios';
import {
  GMUD,
  Aprovador,
  CreateGMUDData,
  UpdateGMUDData,
  CreateAprovadorData,
  UpdateAprovadorData,
  HistoricoGMUD,
} from '../types';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 30000, // Aumentado para 30 segundos
});

// Função para obter o email do usuário logado
const getUserEmail = (): string | null => {
  try {
    const authData = localStorage.getItem('authData');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.user?.email || null;
    }
  } catch (error) {
    console.error('Erro ao obter email do usuário:', error);
  }
  return null;
};

// Função para definir o email do usuário atual
let currentUserEmail: string | null = null;

export const setUserEmail = (email: string | null) => {
  currentUserEmail = email;
};

// Interceptor para adicionar headers de autenticação
api.interceptors.request.use(
  config => {
    const userEmail = currentUserEmail || getUserEmail();
    if (userEmail) {
      config.headers['x-user-email'] = userEmail;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  response => response,
  error => {
    if (error.code === 'ECONNABORTED') {
      console.error('⏰ Timeout da requisição:', error.config?.url);
      console.error('🔧 Verifique se o backend está rodando em:', error.config?.baseURL);
    } else if (error.response) {
      // Servidor respondeu com status de erro
      console.error('❌ Erro do servidor:', error.response.status, error.response.data);
    } else if (error.request) {
      // Requisição foi feita mas não houve resposta
      console.error('🌐 Erro de rede:', error.message);
      console.error('🔧 Verifique se o backend está rodando em:', error.config?.baseURL);
    } else {
      // Algo aconteceu na configuração da requisição
      console.error('⚙️ Erro de configuração:', error.message);
    }

    if (error.response?.data?.error) {
      console.error('API Error:', error.response.data.error);
    }
    return Promise.reject(error);
  }
);

// Serviços de GMUD
export const gmudService = {
  // Listar todos os GMUDs
  list: async (): Promise<GMUD[]> => {
    const response = await api.get('/gmuds');
    return response.data;
  },

  // Buscar GMUD por ID
  getById: async (id: number): Promise<GMUD> => {
    const response = await api.get(`/gmuds/${id}`);
    return response.data;
  },

  // Criar novo GMUD
  create: async (data: CreateGMUDData): Promise<{ id: number; message: string }> => {
    const response = await api.post('/gmuds', data);
    return response.data;
  },

  // Atualizar GMUD
  update: async (id: number, data: UpdateGMUDData): Promise<{ message: string }> => {
    const response = await api.put(`/gmuds/${id}`, data);
    return response.data;
  },

  // Deletar GMUD
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/gmuds/${id}`);
    return response.data;
  },

  // Buscar histórico de um GMUD
  getHistorico: async (id: number): Promise<HistoricoGMUD[]> => {
    const response = await api.get(`/gmuds/${id}/historico`);
    return response.data;
  },

  // Atualizar status de um GMUD
  updateStatus: async (
    id: number,
    status: string,
    usuario: string,
    motivo_cancelamento?: string
  ): Promise<{ message: string }> => {
    const data: any = { status, usuario };
    if (motivo_cancelamento) {
      data.motivo_cancelamento = motivo_cancelamento;
    }
    const response = await api.patch(`/gmuds/${id}/status`, data);
    return response.data;
  },

  // Clonar GMUD
  clone: async (
    id: number,
    motivo_cancelamento?: string
  ): Promise<{ message: string; nova_gmud_id: number; gmud_original_id: number }> => {
    const response = await api.post(`/gmuds/${id}/clone`, { motivo_cancelamento });
    return response.data;
  },
};

// Serviços de Aprovador
export const aprovadorService = {
  // Listar todos os aprovadores
  list: async (ativo?: boolean): Promise<Aprovador[]> => {
    const params = ativo !== undefined ? { ativo: ativo.toString() } : {};
    const response = await api.get('/aprovadores', { params });
    return response.data;
  },

  // Buscar aprovador por ID
  getById: async (id: number): Promise<Aprovador> => {
    const response = await api.get(`/aprovadores/${id}`);
    return response.data;
  },

  // Criar novo aprovador
  create: async (data: CreateAprovadorData): Promise<{ id: number; message: string }> => {
    const response = await api.post('/aprovadores', data);
    return response.data;
  },

  // Atualizar aprovador
  update: async (id: number, data: UpdateAprovadorData): Promise<{ message: string }> => {
    const response = await api.put(`/aprovadores/${id}`, data);
    return response.data;
  },

  // Desativar aprovador
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/aprovadores/${id}`);
    return response.data;
  },

  // Reativar aprovador
  reativar: async (id: number): Promise<{ message: string }> => {
    const response = await api.patch(`/aprovadores/${id}/reativar`);
    return response.data;
  },
};

// Serviços de Auditoria
export const auditoriaService = {
  // Listar logs de auditoria
  list: async (params?: {
    action?: string;
    userId?: string;
    ip?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    page?: number;
  }): Promise<{
    logs: Array<{
      id: number;
      acao: string;
      detalhes: Record<string, unknown>;
      usuario_id: string;
      ip_address: string;
      user_agent: string;
      timestamp: string;
      contexto: any;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> => {
    const response = await api.get('/auditoria', { params });
    return response.data;
  },

  // Buscar auditoria de um GMUD específico
  getGMUDAudit: async (
    gmudId: number
  ): Promise<
    Array<{
      id: number;
      acao: string;
      detalhes: Record<string, unknown>;
      usuario_id: string;
      ip_address: string;
      user_agent: string;
      timestamp: string;
      contexto: any;
    }>
  > => {
    const response = await api.get(`/auditoria/gmud/${gmudId}`);
    return response.data;
  },

  // Buscar estatísticas de auditoria
  getStats: async (
    days?: number
  ): Promise<{
    actionsStats: Array<{
      acao: string;
      count: number;
    }>;
    usersStats: Array<{
      usuario_id: string;
      count: number;
    }>;
    dailyStats: Array<{
      date: string;
      count: number;
    }>;
    period: string;
  }> => {
    const response = await api.get('/auditoria/stats', { params: { days } });
    return response.data;
  },

  // Exportar logs de auditoria para CSV
  exportCSV: async (params?: {
    action?: string;
    userId?: string;
    ip?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<Blob> => {
    const response = await api.get('/auditoria/export/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

export default api;
